import * as React from 'react';
import { useAuth } from '@/hooks/use-auth';

/**
 * Session bridge.
 * Adapts the MSAL-backed `useAuth` contract to the simple Session/User
 * surface consumed by layout (AppShell, ProfileMenu).
 *
 * Role information is no longer hardcoded here — use `useStaffSession()`
 * for the real and effective staff role from `/api/auth/me`.
 * The `UserRole` type is kept for route-registry backward compatibility.
 */

export type UserRole = 'viewer' | 'editor' | 'admin';

export type SessionStatus = 'loading' | 'signedIn' | 'signedOut';

export interface SessionUser {
  name: string;
  email: string;
  initials: string;
  avatarUrl?: string;
}

export type Session =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | { status: 'signedIn'; user: SessionUser };

interface SessionContextValue {
  session: Session;
  signIn: () => void;
  signOut: () => void;
}

const SessionContext = React.createContext<SessionContextValue | null>(null);

function initialsFrom(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0]!}${parts[1]![0]!}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0]!.length >= 2) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  return (email.slice(0, 2) || 'U').toUpperCase();
}

const LOCAL_DEVELOPER: SessionUser = {
  name: 'Local Developer',
  email: 'dev@localhost',
  initials: 'LD',
};

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { enabled, ready, isAuthenticated, account, login, logout } = useAuth();

  const session = React.useMemo<Session>(() => {
    if (!enabled) {
      return { status: 'signedIn', user: LOCAL_DEVELOPER };
    }
    if (!ready) {
      return { status: 'loading' };
    }
    if (!isAuthenticated || !account) {
      return { status: 'signedOut' };
    }
    const name = account.name || account.email || 'User';
    const email = account.email || '';
    return {
      status: 'signedIn',
      user: {
        name,
        email,
        initials: initialsFrom(name, email),
      }
    };
  }, [enabled, ready, isAuthenticated, account]);

  const value = React.useMemo<SessionContextValue>(
    () => ({
      session,
      signIn: login,
      signOut: logout
    }),
    [session, login, logout]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = React.useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within UserProvider');
  }
  return ctx;
}

/** Convenience helper for admin-only surfaces. */
export function useUser(): SessionUser | null {
  const { session } = useSession();
  return session.status === 'signedIn' ? session.user : null;
}
